import axios, { AxiosResponse } from 'axios';
import { UserDto } from '../../models/userDto.types';
import {
  UserM,
  IUserEvent,
  AccountTypeEnum,
  IUsersResponse,
  ImportUsersStatus,
  UserDeleteStatus,
} from '../../models/user.types';
import { IUserProfile, IPasswordPolicy } from '../../components/UserProfile/IUserProfile.types';
import { LoginSecurityEvent } from '../../pages/User/UserDetails/UserDetails.types';
import EnvWindow from '../../utils/env';
import { TokenResponse } from '../../pages/EmailActivation/TokenResponse.types';
import {
  ActivationAccountResponse,
  IdentityClientResponse,
  SecurityEventTypeEnum,
  TwoFactorAuthResponse,
} from './users.types';
import { addParam, downloadFile, splitParams } from '../../utils/servicesHelper';
import { ExternalSystem } from '../../components/SystemSettings/ConfigurationApi/configurationApi.types';

interface UserQueryParams {
  sortByProperty?: string;
  isDescending?: boolean;
  expirationDate?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  pesel?: string;
  phone?: string;
  phoneHidden?: boolean;
  accountTypeFilter?: string;
  examCommitteeFilter?: string;
  accountStatus?: string;
  status?: string;
  examinerRoute?: boolean;
}

export const getParams = (params: UserQueryParams) => {
  const phone = params.phone?.replace(/\s/g, '').replace('+48', '*48');
  const email = params.email?.replace('+', '%2B');

  let urlParams = '';
  urlParams += addParam('examinerExport', params.examinerRoute ? 'true' : '');
  urlParams += addParam('firstName', params.firstName ? params.firstName : '');
  urlParams += addParam('lastName', params.lastName ? params.lastName : '');
  urlParams += addParam('email', email ? email : '');
  urlParams += addParam('pesel', params.pesel ? params.pesel : '');
  urlParams += addParam('phone', phone ? phone : '');
  urlParams += splitParams('accountTypeFilter', params.accountTypeFilter ? params.accountTypeFilter : '');
  urlParams += splitParams('examCommitteeFilter', params.examCommitteeFilter ? params.examCommitteeFilter : '');
  urlParams += addParam(
    'emailConfirmed',
    params.accountStatus !== '' &&
      params.accountStatus !== 'Aktywne,Nieaktywne' &&
      params.accountStatus !== 'Nieaktywne,Aktywne'
      ? params.accountStatus === 'Aktywne'
        ? 'true'
        : 'false'
      : '',
  );

  urlParams += addParam(
    'sortByProperty',
    params.sortByProperty === 'status'
      ? 'isOnline'
      : params.sortByProperty === 'examCommittee'
      ? 'examCommitteeShortName'
      : params.sortByProperty !== 'accountStatus' && params.sortByProperty !== ''
      ? params.sortByProperty
      : 'emailConfirmed',
  );

  urlParams += addParam('isDescending', params.isDescending ? 'true' : 'false');
  urlParams += addParam('expirationDate', params.expirationDate ? params.expirationDate : '');
  if (params.status && ['online', 'offline'].includes(params.status)) {
    urlParams += addParam('isOnline', params.status === 'online' ? 'true' : 'false');
  }

  return urlParams;
};

export async function getUserDetails(id: string): Promise<UserM> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/${id}`);
  return response.data;
}

export async function getAccountDetails(id: string, eventName: SecurityEventTypeEnum): Promise<IUserEvent> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/User/${id}/SecurityEvent/${eventName}`);
  return response.data;
}

export async function createNewUser(user: UserDto): Promise<AxiosResponse<UserM>> {
  return await axios.post(`${EnvWindow.REACT_APP_USERS_API_URL}/Users`, user);
}

export async function getUserModifiedEvent(id: string): Promise<IUserEvent> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/SecurityEvents/UserModified/${id}`);
  return response.data;
}

export async function getUserCreatedEvent(id: string): Promise<IUserEvent> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/SecurityEvents/UserCreated/${id}`);
  return response.data;
}

export async function getLastLoginEvent(id: string): Promise<AxiosResponse<LoginSecurityEvent>> {
  return await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/SecurityEvents/UserLoggedIn/${id}`);
}

export async function getlastForcePasswordChangeEvent(id: string): Promise<AxiosResponse<LoginSecurityEvent>> {
  return await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/SecurityEvents/UserForcedPasswordChange/${id}`);
}

export async function updateUserDetails(
  id: string,
  firstName: string,
  lastName: string,
  phone: string,
  phoneHidden: boolean,
  email: string,
  accountTypeId: AccountTypeEnum,
  examCommiteeId: number,
  pesel: string,
  expirationDate?: string,
): Promise<AxiosResponse<number>> {
  const convertedExpirationDate = expirationDate ? new Date(expirationDate) : null;

  const response = await axios.put(`${EnvWindow.REACT_APP_USERS_API_URL}/Users`, {
    id: id,
    firstName: firstName,
    lastName: lastName,
    pesel: pesel,
    phone: phone,
    phoneHidden: phoneHidden,
    email: email,
    accountTypeId: accountTypeId,
    examCommitteeId: examCommiteeId,
    expirationDate: convertedExpirationDate,
    emailConfirmed: false,
  });
  return response;
}

export async function getUsersApi(
  signal?: AbortSignal,
  pageSize = 0,
  pageNumber = 20,
  sortByProperty?: string,
  isDescending?: boolean,
  expirationDate?: string,
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
  status?: string,
): Promise<IUsersResponse> {
  const params = getParams({
    sortByProperty,
    isDescending,
    expirationDate,
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status,
  });
  const response = await axios.get(
    `${EnvWindow.REACT_APP_USERS_API_URL}/Users/Query?page=${pageNumber}&pageSize=${pageSize}${params}`,
    { signal: signal ?? undefined },
  );
  return response.data;
}

export async function exportUsersApi(
  examinerRoute: boolean,
  signal?: AbortSignal,
  sortByProperty?: string,
  isDescending?: boolean,
  expirationDate?: string,
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
  status?: string,
): Promise<Blob> {
  const params = getParams({
    sortByProperty,
    isDescending,
    expirationDate,
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status,
    examinerRoute,
  });
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/Export?${params}`, {
    signal: signal ?? undefined,
  });
  return response.data;
}

export async function deactivateAllUsers(
  pageSize = 0,
  pageNumber = 20,
  sortByProperty?: string,
  isDescending?: boolean,
  expirationDate?: string,
  isOnline?: string,
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
): Promise<AxiosResponse<boolean>> {
  const params = getParams({
    sortByProperty,
    isDescending,
    expirationDate,
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status: undefined,
  });
  const response = await axios.put(
    `${EnvWindow.REACT_APP_USERS_API_URL}/Users/deactivateUsersByFilter?page=${pageNumber}&pageSize=${pageSize}${params}`,
  );
  return response.data;
}

export async function passwordChangeAllUsers(
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
): Promise<AxiosResponse<boolean>> {
  const params = getParams({
    sortByProperty: '',
    isDescending: undefined,
    expirationDate: '',
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status: undefined,
  });

  const response = await axios.patch(
    `${EnvWindow.REACT_APP_USERS_API_URL}/Users/password/forcedChange/query?page=${params}`,
  );
  return response.data;
}

export async function deactivateSelectedUsers(selectedUsersIds: string[]): Promise<AxiosResponse<boolean>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/deactivateUsersByIds`, {
    ids: selectedUsersIds,
  });
  return response;
}
export async function deleteUser(userId: string): Promise<AxiosResponse<UserDeleteStatus>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/delete/${userId}`);
  return response;
}

export async function forcePasswordChangeSelectedUsers(selectedUsersIds: string[]): Promise<AxiosResponse<boolean>> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/password/forcedChange`, {
    ids: selectedUsersIds,
  });
  return response;
}

export async function getUserProfile(): Promise<AxiosResponse<IUserProfile>> {
  return await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/currentUser`);
}

export async function updateUserName(firstName: string, lastName: string): Promise<AxiosResponse<string>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/User/Name`, {
    FirstName: firstName,
    LastName: lastName,
  });
}

export async function verifyUserToken(token: string): Promise<TokenResponse> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/user/token?token=${token}`);
  return response.data;
}

export async function revokeUserToken(): Promise<TokenResponse> {
  const response = await axios.post(`${EnvWindow.REACT_APP_USERS_API_URL}/User/token/revoke`);
  return response.data;
}

export async function confirmUpdateUserEmail(token: string): Promise<AxiosResponse<{ success: boolean }>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/user/email`, {
    token: token,
  });
}

export async function confirmUpdateUserPassword(
  token: string,
  password: string,
  email: string,
  pesel: string,
): Promise<AxiosResponse<ActivationAccountResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/user/account`, {
    token: token,
    password: btoa(unescape(encodeURIComponent(password))),
    email: email,
    pesel: pesel,
  });
}

export async function updateUserPasswordAdminConfirm(
  newPassword: string,
  userId: string,
): Promise<AxiosResponse<ActivationAccountResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/user/account/manualConfirm`, {
    userId: userId,
    passwordHash: btoa(unescape(encodeURIComponent(newPassword))),
  });
}

export async function updateUserPassword(
  oldPassword: string,
  newPassword: string,
  IsSendAgainToken: boolean,
  ActionForceByAdmin = false,
): Promise<TwoFactorAuthResponse> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/User/password`, {
    oldPassword: btoa(unescape(encodeURIComponent(oldPassword))),
    newPassword: btoa(unescape(encodeURIComponent(newPassword))),
    IsSendAgainToken: IsSendAgainToken,
    ActionForceByAdmin: ActionForceByAdmin,
  });
  return response.data;
}

export async function updateUserEmail(
  userId: string,
  email: string,
  password: string,
  IsSendAgainToken: boolean,
): Promise<AxiosResponse<TwoFactorAuthResponse>> {
  return await axios.post(`${EnvWindow.REACT_APP_USERS_API_URL}/User/email`, {
    id: userId,
    email: email,
    password: btoa(unescape(encodeURIComponent(password))),
    IsSendAgainToken: IsSendAgainToken,
  });
}

export async function updateUserEmailConfirmSms(
  userId: string,
  email: string,
  token: string,
  tokenId: string,
): Promise<AxiosResponse<TwoFactorAuthResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/Verification/updateUserEmailConfirmSms`, {
    userId: userId,
    newEmail: email,
    twoFactorAuthenticationToken: token,
    tokenId: tokenId,
  });
}

export async function updateUserPasswordConfirmSms(
  oldPassword: string,
  newPassword: string,
  token: string,
  tokenId: string,
  ActionForceByAdmin = false,
): Promise<AxiosResponse<TwoFactorAuthResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/Verification/updateUserPasswordConfirmSms`, {
    oldPassword: btoa(unescape(encodeURIComponent(oldPassword))),
    newPassword: btoa(unescape(encodeURIComponent(newPassword))),
    twoFactorAuthenticationToken: token,
    tokenId: tokenId,
    ActionForceByAdmin: ActionForceByAdmin,
  });
}

export async function updateUserPhone(
  phone: string,
  phoneHidden: boolean | undefined,
  password: string,
): Promise<AxiosResponse<IdentityClientResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/User/telephone`, {
    phoneNumber: phone,
    phoneHidden: phoneHidden,
    password: btoa(unescape(encodeURIComponent(password))),
  });
}

export async function resetUserPassword(
  token: string,
  password: string,
): Promise<AxiosResponse<IdentityClientResponse>> {
  return await axios.put(`${EnvWindow.REACT_APP_USERS_API_URL}/User/password/reset`, {
    token: token,
    password: btoa(unescape(encodeURIComponent(password))),
  });
}

export async function authUserEmail(email: string): Promise<AxiosResponse<IdentityClientResponse>> {
  return await axios.patch(`${EnvWindow.REACT_APP_USERS_API_URL}/User/password/reset/${email}`);
}

export async function getPasswordPolicy(): Promise<AxiosResponse<IPasswordPolicy>> {
  return await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/PasswordPolicy`);
}

export async function putPasswordPolicy(newPolicy: IPasswordPolicy): Promise<AxiosResponse> {
  return await axios.put(`${EnvWindow.REACT_APP_USERS_API_URL}/PasswordPolicy`, { ...newPolicy, regex: null });
}

export async function uploadUsersFile(
  file: File,
  hasAdminOkeExaminerCreatePerm: boolean,
): Promise<AxiosResponse<ImportUsersStatus>> {
  const requestBody = new FormData();
  requestBody.append('file', file);
  const response = await axios({
    method: 'POST',
    url: `${EnvWindow.REACT_APP_USERS_API_URL}/Users/${hasAdminOkeExaminerCreatePerm ? 'Oke' : ''}Import/File`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });
  return response;
}

export const importExaminersFromExternalSystem = async (
  system: ExternalSystem,
  committeeIds: number[],
): Promise<AxiosResponse<boolean>> => {
  const response = await axios.post(
    `${EnvWindow.REACT_APP_USERS_API_URL}/Users/${committeeIds.length === 0 ? 'Oke' : ''}Import/${system}`,
    committeeIds.length > 0 ? committeeIds : undefined,
  );
  return response.data;
};

export const downloadImportReport = (importId: string, fileName: string) => {
  downloadFile(`${EnvWindow.REACT_APP_USERS_API_URL}/Users/Import/${importId}/Report`, fileName);
};

export async function getSmsStats(): Promise<AxiosResponse> {
  return await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Statistic/smsStats`);
}
